import React, { useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, X, SlidersHorizontal } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { ProductCard } from '../components/product/ProductCard';
import { mockProducts, categories, Category } from '../data/mockProducts';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState<Category>('All');
  const [showWICOnly, setShowWICOnly] = useState(false);
  const [showEBTOnly, setShowEBTOnly] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filteredProducts = useMemo(() => {
    return mockProducts.filter(product => {
      const matchesSearch = searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
      const matchesWIC = !showWICOnly || product.isWIC;
      const matchesEBT = !showEBTOnly || product.isEBT;
      return matchesSearch && matchesCategory && matchesWIC && matchesEBT;
    });
  }, [searchQuery, selectedCategory, showWICOnly, showEBTOnly]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(searchQuery ? {
      q: searchQuery
    } : {});
  };
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setShowWICOnly(false);
    setShowEBTOnly(false);
    setSearchParams({});
  };
  const hasActiveFilters = searchQuery || selectedCategory !== 'All' || showWICOnly || showEBTOnly;
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-382">
      <TopNav data-id="element-383" />

      <main className="pb-24 md:pb-8" data-id="element-384">
        {/* Search Header */}
        <div className="bg-white border-b border-gray-200 sticky top-[73px] md:top-[81px] z-30" data-id="element-385">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4" data-id="element-386">
            <form onSubmit={handleSearch} className="relative mb-4" data-id="element-387">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-388" />
              <input type="text" placeholder="Search for products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pl-12 pr-12 text-base focus:ring-2 focus:ring-[#10b981] focus:border-transparent focus:bg-white transition-all" data-id="element-389" />
              {searchQuery && <button type="button" onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-390">
                  <X className="w-5 h-5" data-id="element-391" />
                </button>}
            </form>

            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-4 flex-wrap" data-id="element-392">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-393">
                {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category ? 'bg-[#10b981] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-394">
                    {category}
                  </button>)}
              </div>

              <div className="h-6 w-px bg-gray-200" data-id="element-395" />

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-396">
                <input type="checkbox" checked={showWICOnly} onChange={e => setShowWICOnly(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-id="element-397" />
                <span className="text-sm font-medium text-gray-700" data-id="element-398">
                  WIC Only
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-399">
                <input type="checkbox" checked={showEBTOnly} onChange={e => setShowEBTOnly(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-400" />
                <span className="text-sm font-medium text-gray-700" data-id="element-401">
                  EBT Only
                </span>
              </label>

              {hasActiveFilters && <button onClick={clearFilters} className="text-sm text-[#D97757] font-medium hover:underline" data-id="element-402">
                  Clear all
                </button>}
            </div>

            {/* Mobile Filter Button */}
            <div className="md:hidden flex items-center justify-between" data-id="element-403">
              <button onClick={() => setIsFilterOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700" data-id="element-404">
                <SlidersHorizontal className="w-4 h-4" data-id="element-405" />
                Filters
                {hasActiveFilters && <span className="w-2 h-2 bg-[#D97757] rounded-full" data-id="element-406" />}
              </button>
              <span className="text-sm text-gray-500" data-id="element-407">
                {filteredProducts.length} products
              </span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" data-id="element-408">
          <div className="hidden md:flex items-center justify-between mb-6" data-id="element-409">
            <p className="text-gray-600" data-id="element-410">
              Showing{' '}
              <span className="font-semibold" data-id="element-411">{filteredProducts.length}</span>{' '}
              products
            </p>
          </div>

          <AnimatePresence mode="wait" data-id="element-412">
            {filteredProducts.length > 0 ? <motion.div key={`${selectedCategory}-${showWICOnly}-${showEBTOnly}-${searchQuery}`} variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-413">
                {filteredProducts.map((product, index) => <motion.div key={product.id} variants={itemVariants} data-id="element-414">
                    <ProductCard product={product} index={index} data-id="element-415" />
                  </motion.div>)}
              </motion.div> : <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-center py-16" data-id="element-416">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-417">
                  <Search className="w-8 h-8 text-gray-400" data-id="element-418" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-419">
                  No products found
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-420">
                  Try adjusting your search or filters
                </p>
                <button onClick={clearFilters} className="text-[#10b981] font-semibold hover:underline" data-id="element-421">
                  Clear all filters
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile Filter Drawer */}
      <AnimatePresence data-id="element-422">
        {isFilterOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsFilterOpen(false)} className="fixed inset-0 bg-black/50 z-50 md:hidden" data-id="element-423" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 md:hidden max-h-[80vh] overflow-y-auto" data-id="element-424">
              <div className="p-6" data-id="element-425">
                <div className="flex items-center justify-between mb-6" data-id="element-426">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-427">Filters</h3>
                  <button onClick={() => setIsFilterOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-428">
                    <X className="w-5 h-5 text-gray-500" data-id="element-429" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-430">
                  <div data-id="element-431">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-432">
                      Category
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-433">
                      {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category ? 'bg-[#10b981] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-434">
                          {category}
                        </button>)}
                    </div>
                  </div>

                  <div data-id="element-435">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-436">
                      Benefits
                    </h4>
                    <div className="space-y-3" data-id="element-437">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-438">
                        <input type="checkbox" checked={showWICOnly} onChange={e => setShowWICOnly(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-id="element-439" />
                        <span className="text-base text-gray-700" data-id="element-440">
                          WIC Approved Only
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-441">
                        <input type="checkbox" checked={showEBTOnly} onChange={e => setShowEBTOnly(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-442" />
                        <span className="text-base text-gray-700" data-id="element-443">
                          EBT Eligible Only
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-8" data-id="element-444">
                  <button onClick={clearFilters} className="flex-1 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700" data-id="element-445">
                    Clear All
                  </button>
                  <button onClick={() => setIsFilterOpen(false)} className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-semibold" data-id="element-446">
                    Show {filteredProducts.length} Results
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <BottomNav data-id="element-447" />
    </div>;
}